import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { Trophy, ChevronRight, Calendar, MapPin, Sparkles, Star } from 'lucide-react';
import { renderAIContent, AI_CONTENT_CLASSES } from '@/lib/aiContent';
import WCInternalLinks from '@/components/worldcup/WCInternalLinks';
import { translateGroupName, translateCountryName, wcMatchSlug } from '@/lib/wcTranslations';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { sanitizeHtml } from '@/lib/sanitize';

const API_BASE = '';
const LOCALE_MAP = { tr: 'tr-TR', en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-BR', it: 'it-IT', nl: 'nl-NL', ru: 'ru-RU', uk: 'uk-UA', ar: 'ar-SA', id: 'id-ID' };

function _teamSlug(name) {
  const map = {
    'Türkiye': 'turkiye', 'Brazil': 'brazil', 'France': 'france', 'Argentina': 'argentina',
    'England': 'england', 'Germany': 'germany', 'Spain': 'spain', 'Portugal': 'portugal',
    'Netherlands': 'netherlands', 'Croatia': 'croatia', 'Belgium': 'belgium', 'USA': 'usa',
    'Mexico': 'mexico', 'Canada': 'canada', 'Japan': 'japan', 'South Korea': 'south-korea',
    'Australia': 'australia', 'Morocco': 'morocco', 'Senegal': 'senegal', 'Uruguay': 'uruguay',
    'Colombia': 'colombia', 'Ecuador': 'ecuador', 'Paraguay': 'paraguay',
    'Switzerland': 'switzerland', 'Austria': 'austria', 'Sweden': 'sweden', 'Norway': 'norway',
    'Scotland': 'scotland', 'Iran': 'iran', 'Saudi Arabia': 'saudi-arabia', 'Qatar': 'qatar',
    'Egypt': 'egypt', 'Tunisia': 'tunisia', 'Algeria': 'algeria', 'Ivory Coast': 'ivory-coast',
    'Ghana': 'ghana', 'Congo DR': 'congo-dr', 'South Africa': 'south-africa',
    'Cape Verde Islands': 'cape-verde', 'New Zealand': 'new-zealand', 'Panama': 'panama',
    'Haiti': 'haiti', 'Bosnia & Herzegovina': 'bosnia', 'Czech Republic': 'czech-republic',
    'Jordan': 'jordan', 'Iraq': 'iraq', 'Uzbekistan': 'uzbekistan', 'Curaçao': 'curacao',
  };
  return map[name] || name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

export default function WorldCupGroup() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const locale = LOCALE_MAP[currentLang] || 'en-US';
  const letter = (id || '').replace('grup-', '').replace('group-', '').toLowerCase();
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;
  const teamPath = currentLang === 'tr' ? 'takim' : 'team';
  const grpPath = currentLang === 'tr' ? 'grup' : 'group';

  useEffect(() => {
    if (!letter) return;
    fetch(`${API_BASE}/api/worldcup/group/${letter}?lang=${currentLang}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [letter, currentLang]);

  if (loading) return <LoadingPage />;
  if (!data?.group) return <div className="container mx-auto px-4 py-8 text-slate-400">{t('wc.groupNotFound')}</div>;

  const { group, matches, seo, ai_content } = data;
  const groupName = translateGroupName(group.name, currentLang);
  const teams = group.standings || group.teams || [];
  const isTurkeyGroup = teams.some(tm => tm.team_name === 'Türkiye');
  const matchPathPrefix = currentLang === 'tr' ? 'mac' : 'match';

  return (
    <div data-testid="worldcup-group">
      <WCSubmenu />
      <SEOHead title={seo?.title} description={seo?.description} canonical={seo?.canonical} hreflang={seo?.hreflang} jsonLd={seo?.jsonld} />

      <div className="container mx-auto px-4 max-w-5xl py-6">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
          <Link to={`/${currentLang}`} className="hover:text-slate-300">{t('nav.home')}</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={wcBase} className="hover:text-amber-300 text-amber-400/70">{t('wc.breadcrumb')}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-slate-300">{groupName}</span>
        </div>

        <div className={`rounded-2xl p-6 md:p-8 mb-6 ${isTurkeyGroup ? 'bg-gradient-to-r from-red-950/40 to-slate-900 border border-red-500/20' : 'bg-gradient-to-r from-slate-900 to-slate-800 border border-amber-500/10'}`}>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Trophy className="w-8 h-8 inline mr-3 text-amber-400" />{groupName}
          </h1>
          <p className="text-sm text-slate-400">FIFA {t('wc.breadcrumb')} - {teams.length} {t('wc.teams').toLowerCase()}, {matches.length} {t('wc.matchesLabel')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Standings */}
            <div className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden mb-6">
              <div className="p-4 border-b border-slate-800/50">
                <h2 className="text-sm font-bold text-white uppercase tracking-wider">{t('wc.standings')}</h2>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-500 border-b border-slate-800/30">
                    <th className="text-left px-4 py-2 w-8">#</th>
                    <th className="text-left px-4 py-2">{t('wc.team')}</th>
                    <th className="text-center px-2 py-2">{t('wc.p')}</th>
                    <th className="text-center px-2 py-2">{t('wc.w')}</th>
                    <th className="text-center px-2 py-2">{t('wc.d')}</th>
                    <th className="text-center px-2 py-2">{t('wc.l')}</th>
                    <th className="text-center px-2 py-2">{t('wc.gf')}</th>
                    <th className="text-center px-2 py-2">{t('wc.ga')}</th>
                    <th className="text-center px-2 py-2">{t('wc.gd')}</th>
                    <th className="text-center px-2 py-2 font-bold">{t('wc.pts')}</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.map((tm, i) => {
                    const isTR = tm.team_name === 'Türkiye';
                    return (
                      <tr key={i} className={`border-b border-slate-800/20 ${isTR ? 'bg-red-500/10' : i < 2 ? 'border-l-2 border-l-emerald-500' : ''}`}>
                        <td className="px-4 py-3 text-slate-500 font-medium">{tm.rank || i + 1}</td>
                        <td className="px-4 py-3">
                          <Link to={`${wcBase}/${teamPath}/${_teamSlug(tm.team_name)}`} className="flex items-center gap-2 hover:text-amber-300 transition-colors">
                            <img src={tm.team_logo} alt="" className="w-5 h-5 rounded-sm" />
                            <span className={`font-medium ${isTR ? 'text-red-400' : 'text-slate-200'}`}>{translateCountryName(tm.team_name, currentLang)}</span>
                          </Link>
                        </td>
                        <td className="text-center px-2 py-3 text-slate-400">{tm.played}</td>
                        <td className="text-center px-2 py-3 text-emerald-400">{tm.won}</td>
                        <td className="text-center px-2 py-3 text-slate-400">{tm.drawn}</td>
                        <td className="text-center px-2 py-3 text-rose-400">{tm.lost}</td>
                        <td className="text-center px-2 py-3 text-slate-400">{tm.gf}</td>
                        <td className="text-center px-2 py-3 text-slate-400">{tm.ga}</td>
                        <td className="text-center px-2 py-3 text-slate-400">{tm.gd}</td>
                        <td className="text-center px-2 py-3 font-bold text-white">{tm.points}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Matches */}
            <div className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-800/50">
                <h2 className="text-sm font-bold text-white uppercase tracking-wider"><Calendar className="w-4 h-4 inline mr-2" />{t('wc.schedule')}</h2>
              </div>
              <div className="divide-y divide-slate-800/30">
                {matches.map(m => {
                  const date = m.date ? new Date(m.date) : null;
                  const isTR = m.home_name === 'Türkiye' || m.away_name === 'Türkiye';
                  return (
                    <Link key={m.id} to={`${wcBase}/${matchPathPrefix}/${wcMatchSlug(m.home_name, m.away_name, m.id, currentLang)}`} className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-800/30 transition-colors ${isTR ? 'bg-red-500/5' : ''}`}>
                      <div className="w-16 text-center shrink-0">
                        <p className="text-xs text-slate-400">{date?.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}</p>
                        <p className="text-[10px] text-slate-500">{date?.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <div className="flex-1 flex items-center gap-2">
                        <img src={m.home_logo} alt="" className="w-5 h-5" />
                        <span className={`text-sm ${m.home_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(m.home_name, currentLang)}</span>
                        <span className="text-slate-600 text-xs">vs</span>
                        <span className={`text-sm ${m.away_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(m.away_name, currentLang)}</span>
                        <img src={m.away_logo} alt="" className="w-5 h-5" />
                      </div>
                      <span className="text-[10px] text-slate-500">{m.venue_name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3">{t('wc.teamsLabel')}</h3>
              {teams.map((tm, i) => (
                <Link key={i} to={`${wcBase}/${teamPath}/${_teamSlug(tm.team_name)}`}
                  className={`flex items-center gap-3 p-2 rounded-lg mb-1 transition-colors ${tm.team_name === 'Türkiye' ? 'bg-red-500/10 border border-red-500/20' : 'hover:bg-slate-800/30'}`}>
                  <img src={tm.team_logo} alt="" className="w-8 h-8" />
                  <span className={`text-sm font-medium ${tm.team_name === 'Türkiye' ? 'text-red-400' : 'text-slate-300 hover:text-amber-300'}`}>{translateCountryName(tm.team_name, currentLang)}</span>
                </Link>
              ))}
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-amber-500/20 rounded-xl p-4 shadow-[0_0_10px_rgba(251,191,36,0.05)]">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-bold text-white">{t('wc.aiGroupAnalysis')}</h3>
              </div>
              {ai_content?.group_analysis ? (
                <div className={`${AI_CONTENT_CLASSES} text-xs max-h-[500px] overflow-y-auto`}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderAIContent(ai_content.group_analysis)) }} />
              ) : (
                <p className="text-xs text-slate-400">{t('wc.groupAnalysisSoon')}</p>
              )}
            </div>

            <Link to={`${wcBase}/${currentLang === 'tr' ? 'gruplar' : 'groups'}`} className="block text-center text-xs text-amber-400 hover:text-amber-300 py-2">
              {t('wc.backToGroups')}
            </Link>
          </div>
        </div>

        <WCInternalLinks currentPage={`group-${letter}`} lang={currentLang} />
      </div>
    </div>
  );
}
